import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, toast } from "@pdfking/components"
import { Items } from "@pdfking/tfunc"
import { Trash2Icon } from "lucide-react"

import type { PdfviewSidebarView, SidebarViewDefinition, SidebarViewProps } from "#public"

import { useTranslationTyped } from "../i18n/useTranslation"
import { SIDEBAR_VIEWS } from "../sidebar/Views"
import { SIDEBAR_ACTIVE_CLASS } from "./ToggleButtonClass"
import { TooltipIconButton } from "./TooltipIconButton"

// The engine's own response cache is closed over internally (see
// CreateEngine) — the host passes its reference down explicitly
// (CreatePdfViewer's `cache` option) rather than us reaching for it.
async function nuke_cache(cache: SidebarViewProps["cache"]) {
  await cache?.clear()
  for (const cacheName of await caches.keys()) {
    void caches.delete(cacheName)
  }
}

// `Items` widens record keys to string; the registry's keys are the view
// names by construction.
const VIEW_ITEMS = Items(SIDEBAR_VIEWS) as [PdfviewSidebarView, SidebarViewDefinition][]

/**
 * The viewer's panel buttons for one end of a rail: `top`, the panels;
 * `bottom`, the debug views and — debug on — the cache button. The viewer puts
 * them in its rail (see CreatePdfViewer's `rail`); a host with no document open
 * renders them in the same rail, `on_select` routing to the viewer with that
 * panel.
 */
export function ViewerPanels({
  align,
  cache,
  debug,
  on_select,
  selected,
}: {
  align: "top" | "bottom"
  cache: SidebarViewProps["cache"]
  debug: boolean
  on_select: (view: PdfviewSidebarView | undefined) => void
  selected: PdfviewSidebarView | undefined
}) {
  const panels = useTranslationTyped("filecheck")
  return (
    <>
      <SidebarMenu>
        {VIEW_ITEMS.filter(([, view]) => view.align === align && (debug || !view.debug_only)).map(
          ([name, view]) => (
            <SidebarMenuItem key={name}>
              <SidebarMenuButton
                className={SIDEBAR_ACTIVE_CLASS}
                isActive={selected === name}
                onClick={() => on_select(selected === name ? undefined : name)}
                tooltip={view.label(panels)}
              >
                <view.icon />
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        )}
      </SidebarMenu>
      {/* Wiping the engine's cache is a developer's tool, not something to
          leave under an end user's cursor. */}
      {align === "bottom" && debug && (
        <TooltipIconButton
          onClick={() =>
            nuke_cache(cache)
              .then(() => toast.success("Cache cleared"))
              .catch(() => toast.error("Failed to clear cache"))
          }
          side="right"
          size="icon"
          title="Delete Cache"
          variant="ghost"
        >
          <Trash2Icon className="size-4" />
        </TooltipIconButton>
      )}
    </>
  )
}
